import { useEffect, useState } from "react";
import { api, frDate, openPdf, downloadPdf } from "../lib/api";
import PageHeader from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, IdCard, Search, User, Link2, CheckCircle2, XCircle, Mail } from "lucide-react";

const empty = {
  first_name: "", last_name: "", email: "", phone: "", address: "",
  birth_date: "", member_number: "", join_date: "",
  valid_until: String(new Date().getFullYear()),
  photo_base64: "", status: "actif",
  contribution_paid: false, contribution_amount: 0, notes: "",
};

export default function Membres() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState(empty);
  const [q, setQ] = useState("");

  const load = () => api.get("/members").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openNew = () => { setEdit(null); setForm({ ...empty, member_number: `M-${Date.now().toString().slice(-5)}`, join_date: new Date().toISOString().slice(0,10) }); setOpen(true); };
  const openEdit = (m) => { setEdit(m); setForm({ ...empty, ...m }); setOpen(true); };

  const save = async () => {
    try {
      const payload = { ...form, contribution_amount: Number(form.contribution_amount) || 0 };
      if (edit) await api.put(`/members/${edit.id}`, payload);
      else await api.post("/members", payload);
      toast.success(edit ? "Membre modifié." : "Membre ajouté.");
      setOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const remove = async (m) => {
    if (!window.confirm(`Supprimer ${m.first_name} ${m.last_name} ?`)) return;
    await api.delete(`/members/${m.id}`);
    toast.success("Membre supprimé.");
    load();
  };

  const validate = async (m) => {
    try {
      const res = await api.post(`/members/${m.id}/validate`);
      const email = res.data?.email || {};
      if (email.sent) {
        toast.success(`${m.first_name} ${m.last_name} validé(e). Carte envoyée par email.`);
      } else if (email.reason === "no_email") {
        toast.success(`${m.first_name} ${m.last_name} validé(e). (Pas d'email renseigné)`);
      } else if (email.reason === "email_disabled") {
        toast.success(`${m.first_name} ${m.last_name} validé(e). Service email non configuré.`);
      } else if (email.reason === "send_failed") {
        toast.warning(`${m.first_name} ${m.last_name} validé(e). L'envoi email a échoué (domaine expéditeur à vérifier dans Resend).`);
      } else {
        toast.warning(`${m.first_name} ${m.last_name} validé(e), mais l'envoi email a échoué.`);
      }
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const sendCardEmail = async (m) => {
    try {
      await api.post(`/members/${m.id}/send-card-email`);
      toast.success(`Carte envoyée à ${m.email}.`);
    } catch (e) { toast.error(e.response?.data?.detail || "Envoi impossible"); }
  };

  const reject = async (m) => {
    if (!window.confirm(`Rejeter la candidature de ${m.first_name} ${m.last_name} ?`)) return;
    try {
      await api.delete(`/members/${m.id}`);
      toast.success(`Candidature de ${m.first_name} ${m.last_name} rejetée.`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const copyPublicLink = () => {
    const url = `${window.location.origin}/inscription`;
    navigator.clipboard.writeText(url).then(
      () => toast.success("Lien d'inscription copié dans le presse-papiers."),
      () => toast.error("Impossible de copier — copiez manuellement : " + url)
    );
  };

  const onPhoto = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 2 * 1024 * 1024) { toast.error("Photo trop lourde (max 2 Mo)"); return; }
    const reader = new FileReader();
    reader.onload = () => setForm((s) => ({ ...s, photo_base64: reader.result }));
    reader.readAsDataURL(f);
  };

  const filtered = rows.filter((m) => {
    if (!q) return true;
    const s = `${m.first_name} ${m.last_name} ${m.email} ${m.member_number}`.toLowerCase();
    return s.includes(q.toLowerCase());
  });

  return (
    <div data-testid="membres-page">
      <PageHeader
        eyebrow="Adhérents"
        title="Membres de l'association"
        description="Gérez les adhérents, leurs cotisations et éditez leur carte de membre."
        actions={
          <div className="flex gap-2">
            <Button data-testid="copy-public-link-btn" variant="outline" onClick={copyPublicLink}>
              <Link2 size={16} className="mr-1" /> Lien d'inscription
            </Button>
            <Button data-testid="new-member-btn" onClick={openNew} className="bg-brand-orange hover:bg-[#D04A06] text-white">
              <Plus size={16} className="mr-1" /> Nouveau membre
            </Button>
          </div>
        }
      />

      <div className="bg-white border border-brand-border rounded-xl">
        {rows.some((m) => m.pending_validation) && (
          <div className="bg-amber-50 border-b border-amber-200 text-amber-800 px-4 py-2 text-sm flex items-center gap-2">
            <span className="font-medium">{rows.filter((m) => m.pending_validation).length}</span>
            inscription(s) en attente de validation.
          </div>
        )}
        <div className="flex items-center gap-2 p-3 border-b border-brand-border">
          <Search size={16} className="text-gray-400 ml-2" />
          <Input
            data-testid="search-member-input"
            placeholder="Rechercher un membre..."
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="border-0 focus-visible:ring-0 shadow-none"
          />
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-500 small-caps">
              <tr>
                <th className="text-left py-3 px-4">Membre</th>
                <th className="text-left py-3 px-4">N° adhérent</th>
                <th className="text-left py-3 px-4">Contact</th>
                <th className="text-left py-3 px-4">Cotisation</th>
                <th className="text-left py-3 px-4">Statut</th>
                <th className="text-right py-3 px-4">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={6} className="text-center py-10 text-gray-400">Aucun membre pour l'instant.</td></tr>
              )}
              {filtered.map((m) => (
                <tr key={m.id} className="border-t border-brand-border hover:bg-gray-50">
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-3">
                      {m.photo_base64 ? (
                        <img src={m.photo_base64} alt="" className="w-9 h-9 rounded-full object-cover border border-brand-border" />
                      ) : (
                        <div className="w-9 h-9 rounded-full bg-brand-navy/10 flex items-center justify-center text-brand-navy">
                          <User size={16} />
                        </div>
                      )}
                      <div>
                        <div className="font-medium text-gray-900 flex items-center gap-2">
                          {m.first_name} {m.last_name}
                          {m.pending_validation && (
                            <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100 text-[10px] px-1.5 py-0">EN ATTENTE</Badge>
                          )}
                        </div>
                        <div className="text-xs text-gray-500">Depuis {frDate(m.join_date)}</div>
                      </div>
                    </div>
                  </td>
                  <td className="py-3 px-4 font-mono text-xs text-gray-700">{m.member_number}</td>
                  <td className="py-3 px-4 text-gray-700">
                    <div>{m.email || "-"}</div>
                    <div className="text-xs text-gray-400">{m.phone}</div>
                  </td>
                  <td className="py-3 px-4">
                    {m.contribution_paid
                      ? <Badge className="bg-brand-green text-white">Payée</Badge>
                      : <Badge variant="secondary">En attente</Badge>}
                  </td>
                  <td className="py-3 px-4">
                    <Badge variant="outline" className="capitalize">{m.status}</Badge>
                  </td>
                  <td className="py-3 px-4 text-right space-x-1">
                    {m.pending_validation && (
                      <>
                        <Button data-testid={`validate-${m.id}`} size="sm" variant="outline" onClick={() => validate(m)} className="text-brand-green border-brand-green/30">
                          <CheckCircle2 size={14} className="mr-1" /> Valider
                        </Button>
                        <Button data-testid={`reject-${m.id}`} size="sm" variant="outline" onClick={() => reject(m)} className="text-red-600 border-red-200">
                          <XCircle size={14} className="mr-1" /> Rejeter
                        </Button>
                      </>
                    )}
                    <Button data-testid={`card-${m.id}`} size="sm" variant="outline" onClick={() => openPdf(`/members/${m.id}/card-pdf`)}>
                      <IdCard size={14} className="mr-1" /> Carte
                    </Button>
                    {!m.pending_validation && m.email && (
                      <Button data-testid={`email-card-${m.id}`} size="sm" variant="outline" onClick={() => sendCardEmail(m)} title="Renvoyer la carte par email">
                        <Mail size={14} />
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" onClick={() => openEdit(m)}><Pencil size={14} /></Button>
                    <Button size="sm" variant="ghost" onClick={() => remove(m)}><Trash2 size={14} className="text-red-500" /></Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl" data-testid="member-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-brand-navy">
              {edit ? "Modifier le membre" : "Nouveau membre"}
            </DialogTitle>
          </DialogHeader>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-2">
            <div className="md:col-span-2 flex items-center gap-4">
              <div className="w-20 h-20 rounded-full border border-brand-border overflow-hidden bg-gray-50 flex items-center justify-center">
                {form.photo_base64 ? (
                  <img src={form.photo_base64} alt="" className="w-full h-full object-cover" />
                ) : (
                  <User size={28} className="text-gray-400" />
                )}
              </div>
              <div>
                <Label>Photo du membre</Label>
                <Input data-testid="photo-input" type="file" accept="image/*" onChange={onPhoto} className="mt-1" />
                <p className="text-xs text-gray-400 mt-1">JPG ou PNG — 2 Mo max.</p>
              </div>
            </div>

            <div><Label>Prénom *</Label><Input data-testid="first-name-input" value={form.first_name} onChange={(e) => setForm({...form, first_name: e.target.value})} /></div>
            <div><Label>Nom *</Label><Input data-testid="last-name-input" value={form.last_name} onChange={(e) => setForm({...form, last_name: e.target.value})} /></div>
            <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} /></div>
            <div><Label>Téléphone</Label><Input value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} /></div>
            <div className="md:col-span-2"><Label>Adresse</Label><Input value={form.address} onChange={(e) => setForm({...form, address: e.target.value})} /></div>
            <div><Label>Date de naissance</Label><Input type="date" value={form.birth_date || ""} onChange={(e) => setForm({...form, birth_date: e.target.value})} /></div>
            <div><Label>N° d'adhérent</Label><Input value={form.member_number} onChange={(e) => setForm({...form, member_number: e.target.value})} /></div>
            <div><Label>Date d'adhésion</Label><Input type="date" value={form.join_date || ""} onChange={(e) => setForm({...form, join_date: e.target.value})} /></div>
            <div><Label>Valide jusqu'à</Label><Input value={form.valid_until} onChange={(e) => setForm({...form, valid_until: e.target.value})} placeholder="2026" /></div>

            <div><Label>Statut</Label>
              <Select value={form.status} onValueChange={(v) => setForm({...form, status: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="actif">Actif</SelectItem>
                  <SelectItem value="inactif">Inactif</SelectItem>
                  <SelectItem value="suspendu">Suspendu</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Montant cotisation (€)</Label>
              <Input type="number" step="0.01" value={form.contribution_amount} onChange={(e) => setForm({...form, contribution_amount: e.target.value})} />
            </div>

            <div className="md:col-span-2 flex items-center gap-2">
              <Checkbox id="paid" checked={form.contribution_paid} onCheckedChange={(v) => setForm({...form, contribution_paid: !!v})} />
              <Label htmlFor="paid" className="cursor-pointer">Cotisation payée</Label>
            </div>

            <div className="md:col-span-2">
              <Label>Notes</Label>
              <Textarea value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})} rows={2} />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
            <Button data-testid="save-member-btn" onClick={save} className="bg-brand-orange hover:bg-[#D04A06] text-white">
              {edit ? "Enregistrer" : "Créer le membre"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
